import type { Game, Provider, ProviderSearchResult } from "../types/game";

interface IgdbGame {
  id: number;
  name: string;
  summary?: string;
  first_release_date?: number;
  rating?: number;
  cover?: { url: string };
  platforms?: Array<{ name: string }>;
  genres?: Array<{ name: string }>;
  keywords?: Array<{ name: string }>;
  screenshots?: Array<{ url: string }>;
  multiplayer_modes?: Array<{
    onlinecoop?: boolean;
    offlinecoop?: boolean;
    splitscreen?: boolean;
    onlinemax?: number;
    offlinemax?: number;
  }>;
}

function fixImg(url?: string): string | undefined {
  if (!url) return undefined;
  return (url.startsWith("//") ? `https:${url}` : url).replace(
    "t_thumb",
    "t_cover_big",
  );
}

function normalize(g: IgdbGame): Game {
  return {
    id: `igdb:${g.id}`,
    source: "igdb",
    sourceId: String(g.id),
    title: g.name,
    description: g.summary,
    platforms: g.platforms?.map((p) => p.name) ?? [],
    genres: g.genres?.map((x) => x.name) ?? [],
    tags: g.keywords?.map((k) => k.name),
    releaseYear: g.first_release_date
      ? new Date(g.first_release_date * 1000).getUTCFullYear()
      : undefined,
    imageUrl: fixImg(g.cover?.url),
    rating: g.rating,
    screenshots: g.screenshots?.map((s) => fixImg(s.url)!).filter(Boolean),
  };
}

export const igdbProvider: Provider = {
  id: "igdb",
  label: "IGDB",
  blurb:
    "Richest metadata (multiplayer_modes!) but server-only — requires the Cloudflare Pages Function proxy.",
  async search(query: string): Promise<ProviderSearchResult> {
    const res = await fetch("/api/igdb", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(
        `IGDB proxy failed: ${res.status} ${res.statusText} — ${text}`,
      );
    }
    const raw = (await res.json()) as IgdbGame[];
    return { raw, games: raw.map(normalize) };
  },
  notes: {
    requiresApiKey: true,
    callableFromBrowser: "with-proxy",
    corsStatus: "blocked",
    needsProxy: true,
    provides: [
      "title",
      "summary/description",
      "platforms",
      "genres",
      "keywords (tags)",
      "release date",
      "cover image",
      "screenshots",
      "rating",
      "multiplayer_modes (online co-op, offline co-op, split-screen, max players)",
    ],
    missing: [
      "crossplay (not modeled)",
      "Game Pass / PS Plus availability",
      "real-time store pricing",
    ],
    viability: "good",
    summary:
      "Best source for multiplayer/co-op flags. Twitch OAuth client_secret must stay on the server, so it has to be proxied via functions/api/igdb.ts. Slightly more setup, but the richest data of the three.",
  },
};
